/* eslint-disable react/jsx-props-no-spreading */
import { useContext, useEffect, useState } from 'react';
import {
  ModalGeneric,
  SelectGeneric,
  DateInput,
  Button,
  Chips
} from 'ypf-pas-general-ui-components';
import { BsPaperclip } from 'react-icons/bs';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';

import { FilesSdsPtContext } from '../../../../../../context/FilesSdsPtContext';
import useToast from '../../../../../../hooks/useToast';
import { getPosLin } from '../../../../../../services/serviceRequest';
import { postWorkReportFiles } from '../../../../../../services/serviceRequestAttachment';
import { getLocationFile, getDateFile } from '../../../../../../services/monthlyServiceRequest';
import ErrorMessage from '../../../../../../components/ErrorMessage/ErrorMessage';
import { formatDate, formatFileNames } from '../../../../../../utils/formatUtils';
import { assignmentTypeEnum } from '../../../../../../utils/dataStatus';
import filesTableTypes from '../../../../../../utils/filesTableTypes';
import { DetailRequest, DetailRequestMonthly } from '../../../../../../utils/globalPropTypes';
import { MAX_FILE_SIZE } from '../../../../../../constants/constants';
import './ModalAddFile.scss';

const validationSchema = Yup.object().shape({
  posLin: Yup.object().required('Debe elegir una línea'),
  date: Yup.string().required('Debe ingresar una fecha')
});

const formatOption = (optionValue) => ({
  label: optionValue
});

const ModalAddFile = ({ showModal, setShowModal, details, canAddFile, type }) => {
  const { setNewToast, closeToast } = useToast();
  const { dataFilesPt } = useContext(FilesSdsPtContext);
  const { values, errors, setFieldValue, resetForm } = useFormik({
    initialValues: {
      posLin: '',
      date: '',
      location: '',
      dateMonthly: ''
    },
    validationSchema
  });
  const { id } = useParams();
  const [chipOptions, setChipOptions] = useState([]);
  const [posLinOptions, setPosLinOptions] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [locationOptions, setLocationOptions] = useState([]);
  const [dateOptions, setDateOptions] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  const { getDataFilesPt } = useContext(FilesSdsPtContext);
  const [error, setError] = useState({
    open: false,
    message: ''
  });

  const onDropAccepted = (acceptedFiles) => {
    setError({
      open: false,
      message: ''
    });
    const files = dataFilesPt;
    // Archivos repetidos
    const repeatedAttachments = acceptedFiles.filter(
      (adj) =>
        files?.some(
          (fileAdj) => fileAdj.nombre === adj.name || fileAdj.nombre === `${id}_${adj.name}`
        ) || uploadedFiles.some((fileAdj) => fileAdj.name === adj.name)
    );
    // Archivos NO repetidos
    const noRepeatedAttachments = acceptedFiles.filter(
      (adj) =>
        !files.some(
          (fileAdj) => fileAdj.nombre === adj.name || fileAdj.nombre === `${id}_${adj.name}`
        ) && !uploadedFiles.some((fileAdj) => fileAdj.name === adj.name)
    );

    // No se cargan los archivos repetidos
    // se notifica cuáles archivos son
    if (repeatedAttachments.length) {
      const quantitiesAttachments = repeatedAttachments.length > 1;
      const messageErrorAttachment = `${
        quantitiesAttachments
          ? `Los archivos
        ${repeatedAttachments.map(
          (repAdj, ind) => `${repAdj.name} ${ind + 1 === repeatedAttachments.length ? ',' : '-'}`
        )} ya se encuentran cargados`
          : `El archivo ${repeatedAttachments[0].name} ya se encuentra cargado`
      }`;

      setError({
        open: true,
        message: messageErrorAttachment
      });

      setNewToast({
        type: 'ERROR',
        message: messageErrorAttachment
      });
    }
    // Se cargan los archivos no repetidos
    setUploadedFiles([...uploadedFiles, ...noRepeatedAttachments]);
  };

  const onDropRejected = (rejectedFiles) => {
    setError({
      open: false,
      message: ''
    });
    const files = rejectedFiles.map(({ file }) => file);
    const fileNames = formatFileNames(files);
    const messageErrorSize = `
    ${
      rejectedFiles.length > 1
        ? `Los archivos ${fileNames} no se pueden cargar`
        : `El archivo ${fileNames} no se pudo cargar`
    }
  `;

    setNewToast({
      type: 'ERROR',
      message: messageErrorSize
    });
    setError({
      open: true,
      message: messageErrorSize
    });
  };
  const { getInputProps, getRootProps, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxSize: MAX_FILE_SIZE,
    noClick: true,
    noKeyboard: true,
    accept: 'pdf,.PDF,.jpg,.JPG,.jpeg,.JPEG,.msg,.xls,.xlsx,.png,.doc,.docx,.pptx,.ppt'
  });

  const hideModal = () => {
    setError({
      open: false,
      message: ''
    });
    closeToast();
    setShowModal(false);
    resetForm();
    setUploadedFiles([]);
  };

  const isMonthly = details?.tipoAsignacion.id === assignmentTypeEnum.Mensualizada;

  const uploadFiles = async () => {
    setError({
      open: false,
      message: ''
    });
    setDisableButton(true);
    setNewToast({
      type: 'INFO',
      message: 'Adjuntando archivos, por favor espere.',
      options: {
        position: 'top-right',
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined
      }
    });
    const archivosAdjuntos = new FormData();

    uploadedFiles?.forEach((file) => archivosAdjuntos.append('archivosAdjuntos', file));
    try {
      await postWorkReportFiles(
        values?.posLin?.id,
        values?.date || values?.dateMonthly?.id,
        archivosAdjuntos,
        isMonthly,
        values?.location?.id
      );
      hideModal();
      closeToast();
      setNewToast({
        type: 'SUCCESS',
        message: 'Archivos subidos con éxito.'
      });
      getDataFilesPt(id);
    } catch (err) {
      setError({
        open: true,
        message: err.message
      });
      closeToast();
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
    setDisableButton(false);
  };

  useEffect(() => {
    const newChipOptions = uploadedFiles.map((file) => formatOption(file.name));

    setChipOptions(newChipOptions);
  }, [uploadedFiles]);

  useEffect(() => {
    const isPtType = type === filesTableTypes.pt;

    if (canAddFile && isPtType) {
      getPosLin(id, isMonthly)
        .then((data) => {
          setPosLinOptions(data);
        })
        .catch((err) => {
          setNewToast({
            type: 'ERROR',
            message: err.message
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setFieldValue('location', '');
    setFieldValue('dateMonthly', '');
    if (values.posLin?.value) {
      getLocationFile(id, values.posLin.value).then((ubicaciones) => {
        setLocationOptions(ubicaciones);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.posLin?.value]);

  useEffect(() => {
    setFieldValue('dateMonthly', '');
    if (values.posLin?.value && values.location?.value) {
      getDateFile(values.posLin?.id, values.location?.id).then((fechas) => {
        const newDateAdapter = fechas.map((fecha) => ({
          value: fecha,
          label: formatDate({
            value: fecha
          }),
          id: fecha,
          nombre: formatDate({
            value: fecha
          })
        }));

        setDateOptions(newDateAdapter);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.location?.value]);

  useEffect(() => {
    if (!showModal) {
      setLocationOptions([]);
      setDateOptions([]);
    }
  }, [showModal]);

  return (
    <>
      <ModalGeneric
        backdrop="static"
        infoModal
        onHide={hideModal}
        primaryAction={uploadFiles}
        primaryActionDisabled={
          !(
            (values?.posLin?.value && values?.date) ||
            (values?.location?.value && values?.dateMonthly && values?.posLin?.value)
          ) ||
          !uploadedFiles.length ||
          disableButton
        }
        primaryActionLabel="Agregar"
        secondaryAction={hideModal}
        secondaryActionLabel="Salir"
        show={showModal}
        size="lg"
        title="Agregar adjunto"
      >
        <>
          <div className="content-text-center">
            <p>Seleccioná a qué posición, línea y fecha corresponde el archivo</p>
          </div>

          <div
            className={`content-fields mt-4 ${
              details?.tipoAsignacion?.id === assignmentTypeEnum.Mensualizada && 'monthly'
            }`}
          >
            <div className="position-line">
              <SelectGeneric
                label="Posición / Línea."
                name="posLin"
                onChange={(val) => setFieldValue('posLin', val)}
                options={posLinOptions}
                placeholder="Seleccione una opción"
                value={values?.posLin}
                withSelect="100%"
              />
            </div>
            {details?.tipoAsignacion?.id === assignmentTypeEnum.Mensualizada && (
              <div className="styles-column-location-monthly">
                <SelectGeneric
                  defaultValue={null}
                  defaultValueOneOption={false}
                  isDisabled={!values.posLin?.value}
                  label="Ubicación."
                  name="location"
                  onChange={(val) => setFieldValue('location', val)}
                  options={locationOptions}
                  placeholder="Seleccione una opción"
                  value={values?.location}
                  withSelect="100%"
                />
              </div>
            )}
            <div {...getRootProps()} className="styles-half-columns-button" role="none">
              <input {...getInputProps()} name="file" value={undefined} />

              <Button
                className="style-button"
                colorIcon="primary"
                fontSizeLabel="15px"
                icon={BsPaperclip}
                iconRight
                label="Buscar adjunto"
                onClick={open}
                sizeIcon="17px"
                type="button"
                variant="light-gray"
                widthButton="45%"
              />

              {!!uploadedFiles.length && (
                <div className="filenames-container">
                  <Chips
                    data={chipOptions}
                    onDelete={(val) => {
                      // Si solo quedaba una opcion guardo un array vacio. Si quedaba más de una
                      // y val es un array lo guardo tal cual, caso contrario lo guardo como array
                      let newChipOptions;

                      if (chipOptions.length === 1) {
                        newChipOptions = [];
                      } else if (Array.isArray(val) && val.length) {
                        newChipOptions = val;
                      } else {
                        newChipOptions = [val];
                      }
                      const newUploadedFiles = uploadedFiles.filter((file) =>
                        newChipOptions.some((chipOption) => chipOption.label !== file.name)
                      );

                      setUploadedFiles(newUploadedFiles);
                    }}
                    variant="info"
                  />
                </div>
              )}
            </div>
            {details?.tipoAsignacion?.id === assignmentTypeEnum.Mensualizada ? (
              <div className="styles-half-column-date-monthly monthly">
                <SelectGeneric
                  defaultValue={null}
                  defaultValueOneOption={false}
                  isDisabled={!(values.location?.value && values.posLin?.value)}
                  label="Fecha de realización."
                  name="Fecha mensualizada"
                  onChange={(val) => setFieldValue('dateMonthly', val)}
                  options={dateOptions}
                  placeholder="Seleccione una fecha"
                  value={values?.dateMonthly}
                  withSelect="100%"
                />
              </div>
            ) : (
              <div className="styles-half-column-date">
                <DateInput
                  errors={errors?.date}
                  heightDateInput="40px"
                  isInvalid={false}
                  label="Fecha de realización"
                  name="date"
                  onChange={(e) => setFieldValue('date', e.target.value)}
                  placeholder="Seleccione una fecha"
                  value={values?.date}
                  widthDateInput="100%"
                />
              </div>
            )}
          </div>
          {error.open && (
            <div className="error-label-file">
              <ErrorMessage message={error.message} />
            </div>
          )}
        </>
      </ModalGeneric>
    </>
  );
};

ModalAddFile.propTypes = {
  showModal: PropTypes.bool,
  canAddFile: PropTypes.bool,
  setShowModal: PropTypes.func,
  details: PropTypes.oneOfType([DetailRequest, DetailRequestMonthly]).isRequired,
  type: PropTypes.string
};

ModalAddFile.defaultProps = {
  showModal: false,
  canAddFile: false,
  setShowModal: () => {},
  type: ''
};

export default ModalAddFile;
